import { Box, TextField } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmationPopup from "../../../../../../components/common/ConfirmationPopup/ConfirmationPopup";
import { useAppStore } from "../../../../../../hooks/useStores";
import InputLayout from "../../../../../../layout/InputLayout/InputLayout";
import { BrandDefinition } from "../../../../../models/brandDefinition";

interface DuplicateBrandPopupProps {
	brand: BrandDefinition;
	loadBrands: (forcefetchBrands?: boolean) => void;
	setOpenDuplicateBrandModal: (value: boolean) => void;
	openDuplicateBrandModal: boolean;
}

const DuplicateBrandPopup = ({
	brand,
	loadBrands,
	setOpenDuplicateBrandModal,
	openDuplicateBrandModal,
}: DuplicateBrandPopupProps) => {
	const [isDuplicatingBrand, setIsDuplicatingBrand] = useState<boolean>(false);
	const [isValidBrandName, setIsValidBrandName] = useState<boolean>(true);
	const [brandName, setBrandName] = useState<string>("");
	const { brandsDefinitionStore, uiStore } = useAppStore();
	const { t: brandTranslation } = useTranslation("translation", { keyPrefix: "editor.popup.brand" });
	const { t: messagesTranslation } = useTranslation("translation", { keyPrefix: "editor.popup.messages" });
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs.brands" });

	return (
		<>
			<ConfirmationPopup
				onClose={(e) => {
					e.stopPropagation();
					setOpenDuplicateBrandModal(false);
				}}
				title={brandTranslation("title")}
				confirmButtonText={brandTranslation("saveNewBrand")}
				loading={isDuplicatingBrand}
				disableConfirm={isDuplicatingBrand || brandName.trim() === ""}
				open={openDuplicateBrandModal}
				hideCancel={true}
				onClick={async () => {
					if (!brand) return;
					if (!brandName) {
						setIsValidBrandName(false);
						return;
					}
					setIsDuplicatingBrand(true);
					try {
						await brandsDefinitionStore.handleDuplicateBrand({
							...brand,
							name: brandName,
						});

						uiStore.showSnackBarAlert("success", messagesTranslation("savebrand"), 5000);
						setOpenDuplicateBrandModal(false);
						setBrandName("");
						loadBrands(true);
					} catch (error: any) {
						uiStore.showSnackBarAlert(
							"error",
							error?.message || messagesTranslation("failedDuplicate"),
							5000,
						);
						console.error("Duplicate brand failed:", error);
					} finally {
						setIsDuplicatingBrand(false);
					}
				}}
			>
				<Box>
					<InputLayout
						showLabel={false}
						validateOn={!isValidBrandName}
						validationMessage={brandTranslation("brandValidationMessage")}
					>
						<TextField
							value={brandName}
							onChange={(e) => {
								e.stopPropagation();
								setBrandName(e.target.value);
							}}
							placeholder={brandTranslation("input")}
							fullWidth
							error={brandsDefinitionStore.formErrors.invalidName}
							helperText={brandsDefinitionStore.formErrors.invalidName && t("invalidName")}
						/>
					</InputLayout>
				</Box>
			</ConfirmationPopup>
		</>
	);
};

const ObservedComponent = observer(DuplicateBrandPopup);
export default ObservedComponent;
